import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { PortalProvider, usePortal } from './contexts/PortalContext';
import { SignIn } from './pages/SignIn';
import { SignUp } from './pages/SignUp';
import { Dashboard } from './pages/Dashboard';
import { WizardPage } from './pages/wizard/WizardPage';
import { CommitteeDashboard } from './pages/committee/CommitteeDashboard';
import { CommitteeProposal } from './pages/committee/CommitteeProposal';
import { ReviewerQueue } from './pages/reviewer/ReviewerQueue';
import { ReviewForm } from './pages/reviewer/ReviewForm';
import { WIZARD_STEPS, stepPath } from './data/wizardSteps';
import { homeFor } from './data/roles';

/**
 * The path the app is served under, from Vite's BASE_URL — "/" when it owns the
 * origin, "/newui/" when it sits in a folder of the legacy site. React Router
 * wants it without the trailing slash, and an empty string for the root.
 */
const ROUTER_BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

type Role = 'researcher' | 'committee' | 'reviewer';

function Booting() {
  return (
    <div className="boot-screen">
      <p>Loading the research portal…</p>
    </div>
  );
}

/**
 * Gate on the session first, then on the role. Role checks here only decide
 * what to render — the API re-checks every request, so a hand-typed URL buys
 * nothing but an error message.
 */
function Require({ role, children }: { role: Role; children: React.ReactElement }) {
  const { user, booting } = usePortal();
  const location = useLocation();

  if (booting) return <Booting />;
  if (!user) return <Navigate to="/" replace state={{ from: location.pathname }} />;

  const allowed =
    role === 'researcher'
      ? true
      : role === 'committee'
        ? user.isSrcAdmin || user.isIrbAdmin
        : user.isReviewer;

  if (!allowed) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function HomeRedirect() {
  const { user, booting } = usePortal();
  if (booting) return <Booting />;
  if (!user) return <SignIn />;
  return <Navigate to={homeFor(user)} replace />;
}

function PortalRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/sign-up" element={<SignUp />} />

      <Route
        path="/dashboard"
        element={
          <Require role="researcher">
            <Dashboard />
          </Require>
        }
      />

      <Route path="/wizard" element={<Navigate to={stepPath(WIZARD_STEPS[0].id)} replace />} />
      <Route
        path="/wizard/:stepId"
        element={
          <Require role="researcher">
            <WizardPage />
          </Require>
        }
      />

      <Route
        path="/committee"
        element={
          <Require role="committee">
            <CommitteeDashboard />
          </Require>
        }
      />
      <Route
        path="/committee/:researchCode"
        element={
          <Require role="committee">
            <CommitteeProposal />
          </Require>
        }
      />

      <Route
        path="/reviewer"
        element={
          <Require role="reviewer">
            <ReviewerQueue />
          </Require>
        }
      />
      <Route
        path="/reviewer/:researchCode"
        element={
          <Require role="reviewer">
            <ReviewForm />
          </Require>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <PortalProvider>
      <BrowserRouter basename={ROUTER_BASE}>
        <PortalRoutes />
      </BrowserRouter>
    </PortalProvider>
  );
}
